import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CreateSparkCampaign } from '../../../../../../service/create-spark-campaign/create-spark-campaign';

export type SpendingPowerValue = 'ALL' | 'HIGH';

@Component({
  selector: 'targeting-detailed',
  imports: [ReactiveFormsModule],
  templateUrl: './detailed.html',
  styleUrl: './detailed.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Detailed {
  expanded = signal<boolean>(false);

  readonly createSparkCampaignService = inject(CreateSparkCampaign);

  readonly smartInterestBehaviorControl = new FormControl<boolean>(false, { nonNullable: true });

  readonly spendingPowerOptions: { value: SpendingPowerValue; label: string }[] = [
    { value: 'ALL', label: 'All' },
    { value: 'HIGH', label: 'High' },
  ];

  constructor() {
    const svc = this.createSparkCampaignService;
    this.smartInterestBehaviorControl.setValue(svc.smart_interest_behavior_enabled(), { emitEvent: false });
    this.smartInterestBehaviorControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((checked) => {
      svc.smart_interest_behavior_enabled.set(checked);
    });
  }

  selectSpendingPower(value: SpendingPowerValue): void {
    this.createSparkCampaignService.spending_power.set(value);
  }

  isSpendingPowerSelected(value: SpendingPowerValue): boolean {
    return this.createSparkCampaignService.spending_power() === value;
  }

  spendingPowerSegmentClass(value: SpendingPowerValue, index: number): string {
    const selected = this.isSpendingPowerSelected(value);
    const rounded =
      index === 0
        ? 'rounded-l-md'
        : index === this.spendingPowerOptions.length - 1
          ? 'rounded-r-md'
          : '';
    const overlap = index > 0 ? '-ml-px' : '';
    const base =
      `relative flex min-w-0 flex-1 basis-0 items-center justify-center px-3 py-2 text-sm font-semibold focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${overlap} ${rounded}`;

    if (selected) {
      return `${base} z-10 bg-primary text-white shadow-sm inset-ring-1 inset-ring-primary dark:bg-primary dark:inset-ring-primary`;
    }
    return `${base} bg-white text-gray-900 inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-gray-700 dark:hover:bg-white/20`;
  }

  spendingPowerSummaryLabel(): string {
    switch (this.createSparkCampaignService.spending_power()) {
      case 'HIGH':
        return 'High';
      default:
        return 'All';
    }
  }

  smartInterestBehaviorSummaryLabel(): string {
    return this.createSparkCampaignService.smart_interest_behavior_enabled() ? 'Smart on' : 'Smart off';
  }
}
